import { SparklesIcon } from "lucide-react";
import { useCallback } from "react";
import { T, RecordProps, TLShape, TLAsset, TLBinding, Editor } from "tldraw";

import {
  BlockBody,
  BlockContainer,
  NodeBlockHeader,
  BlockContainerShapeContext,
} from "@/components/canvas-editor/components/Container";
import {
  CanvasPercentageInput,
  CanvasSwitch,
  CanvasTextArea,
  PercentOperatorSelect,
} from "@/components/canvas-editor/components/Inputs";
import { NodeDocs } from "@/components/canvas-editor/NodeDocs";
import {
  BindableUtil,
  createMigrations,
} from "@/components/canvas-editor/shapes/bindable";
import {
  OpenAIShape,
  openaiType,
  ComparisonOperator,
  comparisonOperators,
  OpenAIProps,
  Dimensions,
  metadataType,
} from "@/components/canvas-editor/shapes/shapes";
import { ToolStateNode } from "@/components/canvas-editor/utils";
import { SimpleTooltip } from "@/components/ui/tooltip";

/**
 * Scored posts per feed per hour, matching `DEFAULT_ADJUDICATIONS_PER_HOUR` in the
 * matcher's quota policy.
 *
 * Duplicated rather than fetched because it is a sentence in a tooltip, not a reading: the
 * live number for a given feed (which can be raised per feed) is on the Rate limits page,
 * which reads it from the history rows themselves.
 */
const RATE_LIMIT_PER_HOUR = 2000;

export function isOpenAIShape(
  shape: TLAsset | TLBinding | TLShape,
): shape is OpenAIShape {
  return shape.type === openaiType;
}

export class OpenAITool extends ToolStateNode {
  static override id = openaiType;

  static override uiToolDef = (editor: Editor) => ({
    id: openaiType,
    icon: SparklesIcon,
    label: OpenAIShapeUtil.docs.title,
    defaultColor: OpenAIShapeUtil.docs.defaultColor,
    // The only node in the palette with a budget attached. Declared on the tool rather
    // than special-cased in the toolbar so the second metered node inherits the badge by
    // saying one word, instead of by someone remembering this existed.
    rateLimited: true,
    onSelect: () => editor.setCurrentTool(openaiType),
    docs: () => (
      <NodeDocs editor={editor} component={OpenAI} util={OpenAIShapeUtil} />
    ),
  });

  override onEnter() {
    this.editor.setCursor({ type: "none", rotation: 0 });
  }

  override onPointerDown() {
    const { currentPagePoint } = this.editor.inputs;

    this.editor.createShape({
      type: openaiType,
      x: currentPagePoint.x,
      y: currentPagePoint.y,
    });

    this.editor.setCurrentTool("select");
  }
}

export class OpenAIShapeUtil extends BindableUtil<OpenAIShape> {
  static override migrations = createMigrations(openaiType);
  static override type = openaiType;

  static override docs = {
    defaultColor: "blue" as const,
    title: "Natural Language",
    description: (
      <div className="flex flex-col gap-2">
        <div>
          Scores a post against a criterion you write in plain language, and
          compares that score against your threshold.
        </div>
        <div>
          <span className="font-medium">Rate limited.</span> Each post is scored
          on a GPU, so every feed gets{" "}
          <span className="font-medium">
            {RATE_LIMIT_PER_HOUR.toLocaleString()} scored posts an hour
          </span>
          . You are charged only for posts this node actually judges, so putting
          cheaper conditions above it &mdash; a language, keyword or author
          filter &mdash; is what keeps a feed inside the budget. Repeats of a
          post you have already judged are free.
        </div>
        <div>
          When the budget runs out the node stops judging until the hour turns
          over. &ldquo;Admit posts when over budget&rdquo; decides what it
          answers in the meantime: off, it matches nothing and your feed goes
          quiet; on, it stops filtering and lets everything through. Your
          feed&rsquo;s Rate limits page shows the hours it ran out, for the last
          30 days.
        </div>
        <div>
          <span className="font-medium">How you word it changes the score.</span>{" "}
          Write a statement about the post, and say what to leave out in the
          same sentence rather than adding a second condition for it &mdash;
          &ldquo;is about the NBA and does not mention trade rumours&rdquo;
          separates posts far more sharply than an &ldquo;is about the NBA&rdquo;
          condition and a &ldquo;mentions trade rumours&rdquo; one side by side,
          and costs one scoring pass instead of two.
        </div>
        <div>
          Exclusions work best as &ldquo;<span className="font-medium">and does
          not …</span>&rdquo;. &ldquo;but not about …&rdquo; also works.
          &ldquo;other than …&rdquo; does not &mdash; it pushes every score
          toward zero, so nothing clears the threshold. Naming something the
          posts never mention is harmless; it will not quietly suppress them.
        </div>
        <div>
          Keep it to one idea and its exclusions. Long criteria stacking four or
          five unrelated clauses score worse than short ones, not better.
        </div>
      </div>
    ),
  };

  override getType() {
    return openaiType;
  }

  static override props: RecordProps<OpenAIShape> = {
    w: T.number,
    minW: T.number,
    isCollapsed: T.boolean.optional(),
    resultState: T.literalEnum("success", "failure", "idle").optional(),
    isReadOnly: T.boolean.optional(),
    h: T.number,
    prompt: T.string,
    operator: T.literalEnum(...comparisonOperators),
    threshold: T.number,
    overQuota: T.boolean.optional(),
    metadata: metadataType,
  };

  override getDefaultProps() {
    return {
      w: 300,
      minW: 400,
      // One row taller than before: the over-budget switch is a new line in the body.
      h: 205,
      prompt: "",
      threshold: 0.75,
      operator: ">=" as const,
      // Absent and false mean the same thing, and the safe thing: over quota this condition
      // matches nothing rather than stopping filtering.
      overQuota: false,
      metadata: undefined,
    };
  }

  override component(shape: OpenAIShape) {
    const selected = this.editor.getSelectedShapeIds();
    const isSelected = selected.includes(shape.id);

    return (
      <OpenAI
        shape={shape}
        updateShape={this.updateProps.bind(this)}
        isBound={this.getBoundToFeed(shape)}
        isSelected={isSelected}
      />
    );
  }
}

function OpenAI({
  isBound,
  shape,
  className,
  updateShape,
  isSelected,
}: {
  isSelected: boolean;
  isBound: boolean;
  shape: OpenAIShape;
  className?: string;
  updateShape: (shape: OpenAIShape, props: Partial<OpenAIProps>) => void;
}) {
  const onUpdateDimensions = useCallback(
    (dimensions: Pick<Dimensions, "h">) => {
      return updateShape(shape, dimensions);
    },
    [shape, updateShape],
  );

  return (
    <BlockContainerShapeContext value={shape}>
      <BlockContainer
        resultState={shape.props.resultState}
        pattern={shape.props.metadata?.pattern}
        isBound={isBound}
        className={className}
        variant={
          shape.props.metadata?.color || OpenAIShapeUtil.docs.defaultColor
        }
        isSelected={isSelected}
        onUpdateDimensions={onUpdateDimensions}
        dynamicHeight={true}
      >
        <NodeBlockHeader props={shape.props} docs={OpenAIShapeUtil.docs} />

        <BlockBody>
          <div className="flex flex-col gap-2 flex-1 min-w-0">
            <CanvasTextArea
              value={shape.props.prompt}
              placeholder="e.g. is a job posting and does not mention internships"
              maxLength={1000}
              onValueChange={(prompt) => updateShape(shape, { prompt })}
            />

            <div className="flex gap-2">
              <PercentOperatorSelect
                value={shape.props.operator}
                onValueChange={(v) =>
                  updateShape(shape, { operator: v as ComparisonOperator })
                }
              />
              <SimpleTooltip
                variant="markdown"
                message="The confidence threshold. Scores pile up near 0% and near 100% rather than spreading evenly, so most thresholds between them behave alike — 50% and 80% usually select the same posts. If moving it has no effect, move it a long way rather than a little, and check the wording of the criterion above."
              >
                <CanvasPercentageInput
                  aria-label="Threshold"
                  value={shape.props.threshold}
                  placeholder="Threshold"
                  className="flex-1"
                  onValueChange={(value) =>
                    updateShape(shape, { threshold: value })
                  }
                />
              </SimpleTooltip>
            </div>

            <div className="flex items-center justify-between gap-2">
              <SimpleTooltip
                message={`Scoring runs on a GPU, so each feed can have ${RATE_LIMIT_PER_HOUR.toLocaleString()} posts scored an hour. Off: once the budget is gone this condition matches nothing and the feed goes quiet. On: it stops filtering and lets everything through.`}
              >
                <span className="text-xs text-muted-foreground">
                  Admit posts when over budget
                </span>
              </SimpleTooltip>
              <CanvasSwitch
                checked={shape.props.overQuota ?? false}
                onCheckedChange={(overQuota) =>
                  updateShape(shape, { overQuota })
                }
              />
            </div>
          </div>
        </BlockBody>
      </BlockContainer>
    </BlockContainerShapeContext>
  );
}
